import StoreUser from '@/models/StoreUser';
import { getPortalStore } from '@/stores/portal';
import ApiHelper from './ApiHelper';
import CookieHelper from './CookieHelper';

export default class StoreHelper {
  /**
   * ①ストアからセッションIDを取得して、返す
   * ②Cookieからを取得してみて、ストアに保存し、返す
   * ③両方にない場合、nullを返す
   */
  static sessionId() {
    const store = getPortalStore();
    if (store.sessionId) return store.sessionId;
    const lsSessionId = CookieHelper.getSession();
    if (lsSessionId) {
      store.setSessionId(lsSessionId);
      return lsSessionId;
    }
    return null;
  }

  /**
   * ログイン済みのユーザーをストアから取得します。
   * ストアにセットしていない場合、Exceptionを返します。
   */
  static getUser() {
    const store = getPortalStore();
    if (store.user?.userId) return store.user;
    else throw new Error('ユーザーはストアにセットされていません。');
  }

  /**
   * ①ユーザーがストアに入っている場合、それを返す
   * ②セッションIDがある場合、APIからユーザーを取得し、ストアに保存する
   * ③セッションがない場合、nullを返す
   */
  static async getAndSetUser() {
    const store = getPortalStore();
    if (store.user?.userId) return store.user;

    const sessionId = this.sessionId();
    if (!sessionId) {
      return null;
    } else {
      const getUserRes = await ApiHelper.getUser({});
      const themeSettingId = getUserRes.user.themeSettingId;
      const user = StoreUser.fromGetUserResponse(getUserRes);
      store.setUser(user);
      if (themeSettingId) store.setBackgroundTheme(themeSettingId);
      return user;
    }
  }

  /**
   * ユーザ情報を更新する際に、ストアも更新
   */
  static updateUser(displayName?: string, profileImageType?: number) {
    const store = getPortalStore();
    const user = this.getUser();
    const newUserSettings = {
      displayName: !displayName ? displayName : store.user?.displayName,
      profileImageType: profileImageType ? profileImageType : store.user?.profileImageType,
    };
    Object.assign(user, newUserSettings);
    store.setUser(user);
  }

  /**
   * ログイン中のユーザが、自分の情報を更新後に呼び出すことを想定
   * ①セッションIDがある場合、APIからユーザーを取得し、ストアに保存する
   * ②セッションがない場合、nullを返す
   */
  static async setUser() {
    const sessionId = this.sessionId();
    if (!sessionId) {
      return null;
    } else {
      const getUserRes = await ApiHelper.getUser({});
      const user = StoreUser.fromGetUserResponse(getUserRes);
      getPortalStore().setUser(user);
      return user;
    }
  }

  /**
   * ユーザがアプリを使っていない時間を「分」で返します。
   */
  static getIdleTime() {
    const store = getPortalStore();
    const now = new Date();
    const lastUseTime = store.lastUseTime;
    const idleMilliseconds = now.getTime() - lastUseTime.getTime();
    const idleMinutes = idleMilliseconds / (1000 * 60);
    return idleMinutes;
  }

  /**
   * ユーザが最後にアプリを使った時間を更新する
   */
  static updateLastUseTime() {
    getPortalStore().setLastUseTime(new Date());
  }

  static startLoading(opacity?: number) {
    const store = getPortalStore();
    if (opacity) store.setOpacity(opacity);
    else store.setOpacity(0.46);
    store.startLoading();
  }

  static endLoading() {
    getPortalStore().endLoading();
  }

  static get isLoading() {
    return getPortalStore().loading;
  }

  static get opacity() {
    return getPortalStore().opacity;
  }

  static set maintenance(isMaintenance: boolean) {
    getPortalStore().setMaintenance(isMaintenance);
  }

  static get isMaintenance() {
    return getPortalStore().maintenance;
  }

  /**
   * ユーザのポイントを更新する
   */
  static updatePoint(point: number) {
    const user = this.getUser();
    Object.assign(user, { point });
    getPortalStore().setUser(user);
  }
}
